<template>
    <div class="delivery-quotation-letter" title="Request for Quotation">
      <p style="margin: 0px 0px 5px 0;"><em>Dear colleagues,</em></p>
      <p style="margin: 0px 0px 5px 0;">
        <em>We value our cooperation and look forward to long-term partnerships!<br>
          Please provide a quotation for the following request:</em>
      </p>
      <br>
    
      <!-- Transport Direction -->
      <template v-if="quotation.supply_way">
        <p style="margin: 0px 0px 5px 0;"><strong>Transport Direction:</strong> {{ quotation.supply_way }}</p>
      </template>
  
      <!-- Transport Type -->
      <template v-if="quotation.transport_types_id">
        <p style="margin: 0px 0px 5px 0;"><strong>Transport Type:</strong> {{ getReferenceProperty(quotation.transport_types_id) }}</p>
      </template>

      <template v-if="quotation.set_delivery_conditions_ids && quotation.set_delivery_conditions_ids.length">
        <p style="margin: 0px 0px 5px 0;"><strong>Delivery Terms:</strong> {{ quotation.set_delivery_conditions_ids.map(getReferenceProperty).join(', ') }}</p>
      </template>
  
  
      <!-- Loading Location -->
      <template v-if="quotation.loading_places && quotation.loading_places.length">
        <p style="margin: 0px 0px 5px 0;"><strong>Loading Location:</strong></p>
        <ul style="padding-left: 15px;">
          <li v-for="(place, index) in quotation.loading_places" :key="`loading-place-${index}`">
            {{ getReferenceProperty(place.country_id) }}, {{ place.city }}, {{ place.address }}
          </li>
        </ul>
      </template>
  
      <!-- Unloading Location -->
      <template v-if="quotation.unloading_places && quotation.unloading_places.length">
        <p style="margin: 0px 0px 5px 0;"><strong>Unloading Location:</strong></p>
        <ul style="padding-left: 15px;">
          <li v-for="(place, index) in quotation.unloading_places" :key="`unloading-place-${index}`">
            {{ getReferenceProperty(place.country_id) }}, {{ place.city }}, {{ place.address }}
          </li>
        </ul>
      </template>

      <!-- Clearance Location -->
      <template v-if="quotation.clearance_places && quotation.clearance_places.length">
        <p style="margin: 0px 0px 5px 0;"><strong>Clearance Location:</strong></p>
        <ul style="padding-left: 15px;">
          <li v-for="(place, index) in quotation.clearance_places" :key="`clearance-place-${index}`">
            {{ getReferenceProperty(place.country_id) }}, {{ place.city }}, {{ place.address }}
          </li>
        </ul>
      </template>
  
      <!-- Customs Location -->
      <template v-if="quotation.customs_places && quotation.customs_places.length">
        <p style="margin: 0px 0px 5px 0;"><strong>Customs Location:</strong></p>
        <ul style="padding-left: 15px;">
            <li v-for="(place, index) in quotation.customs_places" :key="`customs-place-${index}`">
            {{ getReferenceProperty(place.country_id) }}, {{ place.city }}, {{ place.address }}
            </li>
        </ul>
      </template>

      <!-- Cargo Description -->
      <template v-if="quotation.cargo_name">
        <p style="margin: 0px 0px 5px 0;"><strong>Cargo Name:</strong> {{ tryToTranslate(quotation.cargo_name) }}</p>
      </template>
      <template v-if="quotation.cargo_details">
        <p style="margin: 0px 0px 5px 0;"><strong>Cargo Description:</strong> {{ quotation.cargo_details }}</p>
      </template>      
      <template v-if="quotation.cargo_code">
        <p style="margin: 0px 0px 5px 0;"><strong>HS Code:</strong> {{ tryToTranslate(quotation.cargo_code) }}</p>
      </template>
  
      <template v-if="quotation.adr && quotation.adr != 'Не знаю'">
        <p style="margin: 0px 0px 5px 0;"><strong>{{ tryToTranslate(quotation.adr) }}</strong></p>
      </template>
      <template v-if="quotation.stackable && quotation.stackable != 'Не знаю'">
        <p style="margin: 0px 0px 5px 0;"><strong>{{ tryToTranslate(quotation.stackable) }}</strong></p>
      </template>
      <template v-if="quotation.cargoType && quotation.cargoSize">
        <p style="margin: 0px 0px 5px 0;"><strong>{{ tryToTranslate(quotation.cargoType) }}, {{ tryToTranslate(quotation.cargoSize) }}</strong></p>
      </template>

      <template v-if="quotation.packaging_type">
        <p style="margin: 0px 0px 5px 0;"><strong>Packaging Type:</strong> {{ tryToTranslate(quotation.packaging_type) }}</p>
      </template>
      <template v-if="quotation.dimensions.length || quotation.dimensions.width || quotation.dimensions.height || quotation.dimensions.volume || quotation.gross_weight || quotation.number_of_places">
        <p style="margin: 0px 0px 5px 0;"><strong>Dimensions and Volume:</strong>
          <span>
            {{ [
              quotation.dimensions.length ? `Gross Weight: ${formatNumber(quotation.gross_weight)} кг` : '',
              quotation.dimensions.length ? `Length: ${quotation.dimensions.length} m` : '',
              quotation.dimensions.width ? `Width: ${quotation.dimensions.width} m` : '',
              quotation.dimensions.height ? `Height: ${quotation.dimensions.height} m` : '',
              quotation.dimensions.volume ? `Volume: ${quotation.dimensions.volume} m³` : '',
              quotation.dimensions.volume ? `Number of places: ${quotation.number_of_places}` : ''
            ].filter(Boolean).join(', ') }}
          </span>
        </p>
      </template>
      <template v-if="quotation.dimensions.details">
        <p style="margin: 0px 0px 5px 0;"><strong>Dimensions details:</strong> {{ quotation.dimensions.details }}</p>
      </template>
    
      <!-- Next Transport Date -->
      <template>
        <p style="margin: 0px 0px 5px 0;" v-if="quotation.isIndicative"><strong>This request is indicative; delivery may not occur.</strong></p>
        <p style="margin: 0px 0px 5px 0;" v-if="quotation.next_transport_date"><strong>Next Transport Date: </strong>{{ formattedDate(quotation.next_transport_date)}}</p>
      </template>

      <!-- Required Transport Types -->
      <template v-if="quotation.transport_types_and_kinds && quotation.transport_types_and_kinds.length">
        <p style="margin: 0px 0px 5px 0;"><strong>Required Transport Types:</strong></p>
        <ul style="padding-left: 15px;">
          <li v-for="(transport, index) in quotation.transport_types_and_kinds" :key="`transport_types_and_kinds-${index}`">
            <b style="text-transform: none;">{{ transport.quantity }} pcs</b>&nbsp;{{ tryToTranslate(transport.transport_category_name) }}, {{ tryToTranslate(transport.transport_item_name) }}
          </li>
        </ul>
      </template>    

      <!-- Additional Terms -->
      <template v-if="quotation.additional_terms">
        <p style="margin: 0px 0px 5px 0;"><strong>Additional Terms:</strong> {{ quotation.additional_terms }}</p>
      </template>
        
      <!-- Reverse Loading -->
      <template v-if="quotation.reverse_direction === 'Yes'">
        <p style="margin: 0px 0px 5px 0;"><strong>Reverse loading is available.</strong> {{ quotation.reverse_details }}</p>
      </template>
  
      <!-- Delivery Frequency -->
      <template>
        <p style="margin: 0px 0px 5px 0;"><strong>Delivery Frequency:</strong> {{ getReferenceProperty(quotation.supply_frequency_id) }}, quantity: {{ quotation.supply_count }}</p>
      </template>
  
      <!-- Transit Preferences -->
      <template v-if="quotation.set_transit_directions_ids && quotation.set_transit_directions_ids.length">
        <p style="margin: 0px 0px 5px 0;"><strong>Transit Preferences:</strong> {{ quotation.set_transit_directions_ids.map(getReferenceProperty).join(', ') }}</p>
      </template>
  
      <!-- Partner Requirements -->
      <template v-if="quotation.special_partner_requirements_ids && quotation.special_partner_requirements_ids.length">
        <p style="margin: 0px 0px 5px 0;"><strong>Special Partner Requirements:</strong> {{ quotation.special_partner_requirements_ids.map(getReferenceProperty).join(', ') }}</p>
      </template>
  
      <!-- Cargo Value -->
      <template v-if="quotation.cargo_invoice_value || quotation.cargo_declared_value">
        <p style="margin: 0px 0px 5px 0;">
          <span v-if="quotation.cargo_invoice_value">
            <strong>Invoice Value:</strong> {{ formatNumber(quotation.cargo_invoice_value) }} {{ getReferenceProperty(quotation.cargo_invoice_currency_id) }}
            <span v-if="quotation.cargo_declared_value">, </span>
          </span>
          <span v-if="quotation.cargo_declared_value">
            <strong>Declared Value:</strong> {{ formatNumber(quotation.cargo_declared_value) }} {{ getReferenceProperty(quotation.cargo_declared_currency_id) }}
          </span>
        </p>
      </template>
  
      <!-- Payment Terms -->
      <template v-if="quotation.payment_terms && quotation.payment_terms !== 'Не знаю'">
        <p style="margin: 0px 0px 5px 0;"><strong>Payment Terms:</strong> {{ tryToTranslate(quotation.payment_terms) }}, <span style="text-transform: lowercase;"> 
          <template v-if="quotation.payment_terms === 'Prepayment'">
            prepayment percentage: {{ tryToTranslate(quotation.prepayment_percentage) }}%, {{ tryToTranslate(quotation.prepaymentComment) }}
          </template>
          <template v-if="quotation.payment_terms === 'Postpayment'">
            {{ quotation.payment_from_copies }}, {{ tryToTranslate(quotation.payment_period) }} {{ formatDays(quotation.payment_period) }} (<span style="text-transform: lowercase;">{{ tryToTranslate(quotation.payment_period_type) }}</span>)
          </template>
        </span></p>
      </template>
      <hr>
      <em>We look forward to reviewing your proposal and providing feedback promptly!
      <br>Thank you in advance for your response, and we hope for a mutually beneficial partnership!</em>
  
      <!-- Signature -->
      <div>
        <br>
        --<br>
        <b>Best regards, <br>
        Ivan Ivanovich</b><br>
        Logistics Specialist <br>
        TANAIS JSC<br>
        <br>
        <a href="tel:+79621997929">+7 (962) 199-79-29</a><br>
        <a href="mailto:ivan@eu-tanais.com" class="moz-txt-link-freetext">ivan@eu-tanais.com</a> <br>
        Moscow, Gorbunova St. 2, Building 3<br>
        BC "Grand Setun Plaza"<br>
        <br>
        <img src="http://eu-tanais.com/logo-tanais.jpg" alt="" width="200"><br>
      </div>
    </div>
  </template>
  
  
<script>
    import { formattedDate } from '@/mixins/textFilters';

    export default {
        props: {
            quotation: { type: Object, required: true },
        },
        mixins: [
            formattedDate,
        ],  
        computed: {
            referenceProperties() {
            return this.$store.getters.REFERENCE_PROPERTIES;
            }
        },
        methods: {
            getReferenceProperty(id) {
                console.log("this.referenceProperties", this.referenceProperties);
                return this.referenceProperties[id] ? this.referenceProperties[id].text_eng : 'Unknown';
            },
            formatNumber(value) {
                if (typeof value === 'string') {
                    const number = parseFloat(value.replace(',', '.'));
                    return isNaN(number) ? value : number.toLocaleString();
                }
                return value.toLocaleString();
            },
            formatDays(days) {
                if (days === 1) return 'day';
                return 'days';
            },
            tryToTranslate(text) {
                const vocabulary = {
                    'Не ADR': 'Not ADR',
                    'Штабелируемый': 'Stackable',
                    'Не штабелируемый': 'Not Stackable',
                    'Сборный': 'Consolidated',
                    'Генеральный': 'Full Load',
                    'Габаритный': 'Standard Size',
                    'Не габаритный': 'Oversized',
                    'Календарные дни': 'Calendar Days',
                    'Рабочие дни': 'Business Days',
                    'от копий': 'from Copies',
                    'от оригиналов': 'from Originals',
                    'Предоплата': 'Prepayment',
                    'Постоплата с копий': 'Postpayment from Copies'
                };

                return vocabulary[text] ? vocabulary[text] : text;
            }
        }
    };
</script>

<style lang="scss" scoped>
    .delivery-quotation-letter {
        line-height: 16px;

        p {
            margin-bottom: 12px;
        }
        b, strong {
            text-transform: none;
        }  

        ul { 
            list-style: disc; 
            margin-left: 25px;
        }
    }
</style>